import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Link } from 'react-router-dom';
import { Utensils, Stethoscope, ShoppingBag, ArrowUpRight, Code2, Check, Sparkles } from 'lucide-react';
import { useRef, useState, useCallback } from 'react';
import OptimizedImageWithFallback from '@/components/OptimizedImageWithFallback';

const services = [
  {
    id: 'restaurant',
    route: '/services/restaurant',
    icon: Utensils,
    title: 'Restauration',
    description: 'Automatisez vos réservations, optimisez vos stocks et améliorez l\'expérience client grâce à l\'IA.',
    benefit: 'Jusqu\'à 40% de gain de temps',
    image: '/images/restaurant.webp',
    alt: 'Chef en cuisine professionnelle',
  },
  {
    id: 'medical',
    route: '/services/medical',
    icon: Stethoscope,
    title: 'Services Médicaux',
    description: 'Simplifiez la prise de rendez-vous, automatisez les rappels patients et gérez les dossiers efficacement.',
    benefit: 'Réduction de 60% des no-shows',
    image: '/images/medical.webp',
    alt: 'Médecin utilisant une tablette',
  },
  {
    id: 'retail',
    route: '/services/retail',
    icon: ShoppingBag,
    title: 'Commerce & Retail',
    description: 'Gérez votre inventaire, personnalisez l\'expérience d\'achat et boostez vos ventes avec des outils IA.',
    benefit: '+25% de conversion client',
    image: '/images/retail.webp',
    alt: 'Intérieur de boutique moderne',
  },
];

const webFeatures = [
  'Design responsive & sur mesure',
  'SEO optimisé dès le départ',
  'Performance ultra-rapide',
  'Intégration IA & automatisation',
];

function WebCreationCard() {
  const ref = useScrollReveal();
  const mockupRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    if (!mockupRef.current) return;
    const rect = mockupRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    setTilt({ x, y });
  }, []);

  const resetTilt = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  return (
    <div ref={ref} className="scroll-reveal mt-8 md:mt-12" style={{ transitionDelay: '0.36s' }}>
      <div className="web-creation-card relative overflow-hidden rounded-3xl border border-border/50 bg-card group hover:border-[hsl(211,100%,50%)]/30 hover:shadow-xl transition-all duration-500">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Content */}
          <div className="p-8 md:p-12 flex flex-col justify-center order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-[hsl(211,100%,50%)]/10 dark:bg-[hsl(211,100%,55%)]/10 flex items-center justify-center">
                <Code2 className="w-5 h-5 text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]" />
              </div>
              <span className="text-xs font-semibold tracking-wider uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]">
                Design & Développement
              </span>
            </div>

            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
              Création de Site Web Sur Mesure
            </h3>

            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
              Un site web unique qui reflète votre identité, conçu pour convertir
              vos visiteurs en clients. Design premium, performance optimale et SEO intégré.
            </p>

            <ul className="space-y-3 mb-8">
              {webFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-foreground/80">
                  <div className="w-5 h-5 rounded-full bg-[hsl(211,100%,50%)]/10 dark:bg-[hsl(211,100%,55%)]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]" />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>

            <a
              href="#audit"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('audit')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-premium inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(211,100%,50%)] dark:bg-[hsl(211,100%,55%)] text-white font-medium text-sm w-fit"
            >
              Demander un devis
              <Sparkles className="w-4 h-4" />
            </a>
          </div>

          {/* Interactive Browser Mockup */}
          <div
            className="relative p-6 md:p-12 flex items-center justify-center order-1 lg:order-2"
            onMouseMove={handleMouseMove}
            onMouseLeave={resetTilt}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(211,100%,50%)]/[0.04] via-transparent to-[hsl(211,100%,60%)]/[0.03] pointer-events-none" />

            <div
              ref={mockupRef}
              className="browser-mockup w-full max-w-[380px]"
              style={{
                transform: `perspective(800px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
              }}
            >
              {/* Chrome */}
              <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/60 border-b border-border/50">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                </div>
                <div className="flex-1 mx-2 px-3 py-1 rounded-md bg-background/80 text-[10px] text-muted-foreground font-mono truncate">
                  votresite.tn
                </div>
              </div>

              {/* Animated website being built */}
              <div className="p-3 space-y-2.5 bg-background/50 min-h-[220px]">
                {/* Nav */}
                <div className="web-build-element flex items-center justify-between">
                  <div className="w-16 h-2.5 rounded-full bg-foreground/15" />
                  <div className="flex gap-2">
                    <div className="w-10 h-2 rounded-full bg-foreground/10" />
                    <div className="w-10 h-2 rounded-full bg-foreground/10" />
                    <div className="w-10 h-2 rounded-full bg-foreground/10" />
                  </div>
                </div>

                {/* Hero */}
                <div className="web-build-element rounded-lg bg-gradient-to-br from-[hsl(211,100%,50%)]/15 to-[hsl(211,80%,60%)]/10 p-4 text-center">
                  <div className="w-3/4 h-3 rounded-full bg-foreground/20 mx-auto mb-2" />
                  <div className="w-1/2 h-2 rounded-full bg-foreground/10 mx-auto mb-3" />
                  <div className="w-16 h-5 rounded-full bg-[hsl(211,100%,50%)]/25 mx-auto" />
                </div>

                {/* Cards */}
                <div className="web-build-element grid grid-cols-3 gap-1.5">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="rounded-md bg-card border border-border/30 p-1.5">
                      <div className="w-full h-7 rounded bg-foreground/[0.04] mb-1" />
                      <div className="w-3/4 h-1.5 rounded-full bg-foreground/15 mb-0.5" />
                      <div className="w-full h-1 rounded-full bg-foreground/[0.07]" />
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="web-build-element flex justify-center">
                  <div className="w-20 h-5 rounded-full bg-[hsl(211,100%,50%)]/20" />
                </div>

                {/* Footer */}
                <div className="web-build-element border-t border-border/30 pt-2">
                  <div className="flex justify-between items-center">
                    <div className="w-12 h-1.5 rounded-full bg-foreground/10" />
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-foreground/[0.07]" />
                      <div className="w-3 h-3 rounded-full bg-foreground/[0.07]" />
                      <div className="w-3 h-3 rounded-full bg-foreground/[0.07]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ service, index }) {
  const ref = useScrollReveal();
  const Icon = service.icon;
  const isFirst = index === 0;

  return (
    <Link
      ref={ref}
      to={service.route}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label={`En savoir plus sur ${service.title}`}
      data-testid={`service-card-${service.id}`}
      className="scroll-reveal service-card relative overflow-hidden rounded-3xl border border-border/50 bg-card group cursor-pointer hover:border-[hsl(211,100%,50%)]/50 hover:shadow-lg hover:-translate-y-1"
      style={{ transitionDelay: `${index * 0.12}s` }}
    >
      {/* Image */}
      <div className="relative h-56 md:h-64 overflow-hidden">
        <OptimizedImageWithFallback
          src={service.image}
          alt={service.alt}
          className="service-card-image w-full h-full object-cover group-hover:scale-105 transition-transform duration-600 ease-out"
          loading={isFirst ? "eager" : "lazy"}
          decoding={isFirst ? "sync" : "async"}
          fetchPriority={isFirst ? "high" : undefined}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-[hsl(211,100%,50%)]/10 dark:bg-[hsl(211,100%,55%)]/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]" />
          </div>
          <span className="text-xs font-semibold tracking-wider uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]">
            {service.benefit}
          </span>
        </div>

        <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
          {service.title}
        </h3>

        <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
          {service.description}
        </p>

        <div className="flex items-center gap-2 text-sm font-medium text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] group-hover:gap-3 transition-all duration-300">
          En savoir plus
          <ArrowUpRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

export default function ServicesSection() {
  const titleRef = useScrollReveal();

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div ref={titleRef} className="scroll-reveal text-center mb-16 md:mb-20">
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] mb-4">
            Nos Solutions d'Automatisation IA
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground mb-6">
            Optimisez votre activité avec l'IA
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Des solutions d'automatisation intelligentes adaptées à chaque secteur en France et en Tunisie, pour automatiser
            les tâches répétitives et libérer votre potentiel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>

        <WebCreationCard />
      </div>
    </section>
  );
}
